
import { WorkspaceModelType } from "@stores/WorkspaceStore";
import { selectItemPlain } from "../ui/selectItem";
import { logErrorToSentry } from "../../utils/errors/logErrorToSentry";
import { inAppEventsHub } from "../../volatile/InAppEventsHub";
import { removeLocalDoc } from "@stores/document/DocumentLocal";
import { queryGqlApi } from "../../graphql/queryGqlApi";
import { deleteItemForever as deleteItemForeverGQL } from "../../graphql/mutations";
import { CrudServices } from "../../platform/services/types/ServiceDependencies";
import { AppState } from "@stores/AppState";


export interface DeleteItemDependencies extends CrudServices {
    appState: AppState,
}

/**
 * Permanently deletes an item and (atomically on the server) any subtree it
 * exclusively owns, plus the documents for any LEAF items in that subtree.
 *
 * @param dependencies - Service and store dependencies
 * @param itemId - target item id
 * @param workspaceMST - local workspace store
 * @param _markedToRemove - retained for call-site compatibility; ignored (server is atomic)
 * @param force - allow deleting non-trashed items
 */
export async function deleteItemForever(
    dependencies: DeleteItemDependencies,
    itemId: string,
    workspaceMST: WorkspaceModelType,
    _markedToRemove: Set<string> = new Set(),
    force: boolean = false
): Promise<void> {
    if (workspaceMST.selectedId === itemId) {
        selectItemPlain(dependencies.appState.rootStore, workspaceMST, null);
    }
    const itemMST = workspaceMST.items.get(itemId);
    if (!itemMST) {
        return;
    }
    if (!itemMST.trashed && !force) {
        console.warn('attempt to delete forever non trashed item');
        return;
    }

    inAppEventsHub.dispatch({ type: 'ItemDeleteStart', itemId });

    try {
        const result = await queryGqlApi(
            deleteItemForeverGQL,
            { input: { id: itemId, force } },
            dependencies.dataClientService,
            dependencies.authService,
            'pgServer'
        );
        const deleted: Array<{ id: string }> = Array.isArray(result) ? result : [];
        for (const it of deleted) {
            removeLocalDoc(it.id);
            workspaceMST.removeItemSync(it.id);
        }
    } catch (error) {
        logErrorToSentry(error as Error);
    } finally {
        inAppEventsHub.dispatch({ type: 'ItemDeleteEnd', itemId });
    }
}
